import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { History, Clock, User, ArrowRight, MessageSquare, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_HUE = {
  direction_updated: '#22d3ee',
  direction_considering: '#a78bfa',
  cdp_reviewed: '#38bdf8',
  parent_linked: '#f472b6',
  manual: '#c084fc',
};

const KIND_LABEL = {
  direction_updated: 'Direction updated',
  direction_considering: 'Direction under review',
  cdp_reviewed: 'CDP reviewed',
  parent_linked: 'Parent linked',
  manual: 'Update',
};

/**
 * CDP Revision History, the audit log required to prove compliance and to
 * show parents what changed and when.
 */
export default function CDPRevisionHistory({ studentId, canRequestReview = false }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [requested, setRequested] = useState({});

  useEffect(() => {
    const url = studentId
      ? `${API}/nc/cdp/audit?student_id=${studentId}`
      : `${API}/nc/cdp/audit`;
    axios.get(url, getAuthHeader())
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], total: 0 }));
  }, [getAuthHeader, studentId]);

  const requestReview = async (change) => {
    try {
      const res = await axios.post(`${API}/nc/cdp/request-review`, {
        student_id: studentId,
        change_id: change.id,
        note: `Please review this change: ${change.summary}`,
      }, getAuthHeader());
      setRequested((s) => ({ ...s, [change.id]: true }));
      toast.success(res.data?.is_bump
        ? "Follow-up sent to counselor."
        : "Review request sent to counselor. They'll respond when they have time.");
    } catch {
      toast.error("Could not send review request, try again.");
    }
  };

  if (!data) return null;
  const entries = data.entries || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-violet-400/25 bg-[#0a1729]/60 p-6"
      style={{ boxShadow: '0 30px 80px -60px rgba(167,139,250,0.45)' }}
      data-testid="cdp-revision-history"
    >
      <div aria-hidden className="pointer-events-none absolute -left-20 -bottom-20 h-56 w-56 rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.4), transparent 65%)' }} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-4 w-4 text-violet-300" />
          <h2 className="font-outfit text-base font-semibold text-white">Revision History</h2>
          <span className="text-xs text-slate-300">
            {entries.length === 0 ? 'No changes yet' : `${entries.length} change${entries.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {entries.length === 0 ? (
          <p className="text-sm text-slate-300">
            Every edit to your Career Development Plan is logged here so you, your counselor, and your parent
            can see exactly what changed and when.
          </p>
        ) : (
          <div className="space-y-2.5">
            {entries.slice(0, 15).map((e) => {
              const hue = KIND_HUE[e.kind] || '#22d3ee';
              return (
                <div key={e.id} className="rounded-xl border p-3"
                  style={{ borderColor: `${hue}33`, background: `${hue}0a` }}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider"
                      style={{ borderColor: `${hue}66`, background: `${hue}15`, color: hue }}>
                      {KIND_LABEL[e.kind] || e.kind}
                    </span>
                    <span className="text-[11px] text-slate-300 inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {formatWhen(e.at)}
                    </span>
                    <span className="text-[11px] text-slate-300 inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {e.actor_name} · {e.actor_role}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-white">{e.summary}</p>
                  {(e.before?.career || e.after?.career) && (
                    <p className="mt-1 text-xs text-slate-300 inline-flex items-center gap-1.5">
                      <span>{e.before?.career || '-'}</span>
                      <ArrowRight className="h-3 w-3 text-slate-300" />
                      <span className="text-white">{e.after?.career || '-'}</span>
                    </p>
                  )}
                  {canRequestReview && (
                    <div className="mt-2">
                      {requested[e.id] ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-cyan-300">
                          <Check className="h-3 w-3" /> Sent to counselor
                        </span>
                      ) : (
                        <button
                          onClick={() => requestReview(e)}
                          data-testid={`parent-request-review-${e.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-violet-300/40 bg-violet-500/[0.08] px-2.5 py-1 text-[11px] text-violet-100 hover:bg-violet-500/15 transition-colors"
                        >
                          <MessageSquare className="h-3 w-3" /> Request review from counselor
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}
